import React, { useState, useEffect } from 'react';
import { UserCheck, UserX, Clock, CheckCircle } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLoader } from '../../components/shared/LoaderContext';

export default function ReferralRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [remarks, setRemarks] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const { showLoader, hideLoader } = useLoader();

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/member/referral-requests');
      setRequests(res.data.requests || []);
    } catch {
      toast.error('Failed to fetch referral requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const handleApprove = async (approvalId) => {
    setActionLoading((prev) => ({ ...prev, [approvalId]: 'approve' }));
    const interval = showLoader('Approving referral request...');
    try {
      const res = await api.put(`/referral/approve/${approvalId}`, {
        remarks: remarks[approvalId] || ''
      });
      hideLoader(interval);
      toast.success('Referral approved successfully!');
      if (res.data.bothApproved) {
        toast.success('Both referrals approved! Admin has been notified.');
      }
      fetchRequests();
    } catch (err) {
      hideLoader(interval);
      toast.error(err.response?.data?.message || 'Failed to approve');
    } finally {
      setActionLoading((prev) => ({ ...prev, [approvalId]: null }));
    }
  };

  const handleReject = async (approvalId) => {
    if (!remarks[approvalId]) { toast.error('Please provide a reason for rejection'); return; }
    if (!window.confirm('Are you sure you want to reject this referral request?')) return;
    setActionLoading((prev) => ({ ...prev, [approvalId]: 'reject' }));
    const interval = showLoader('Rejecting referral request...');
    try {
      await api.put(`/referral/reject/${approvalId}`, { remarks: remarks[approvalId] });
      hideLoader(interval);
      toast.success('Referral request rejected');
      fetchRequests();
    } catch (err) {
      hideLoader(interval);
      toast.error(err.response?.data?.message || 'Failed to reject');
    } finally {
      setActionLoading((prev) => ({ ...prev, [approvalId]: null }));
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h2>Referral Requests</h2>
        <p>Review and approve membership referral requests from applicants</p>
      </div>

      {requests.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <CheckCircle size={48} style={{ color: 'var(--success)', margin: '0 auto 16px' }} />
          <h3 style={{ color: 'var(--text-muted)', marginBottom: 8 }}>No Pending Requests</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            You have no pending referral approval requests at this time.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {requests.map((req) => {
            const applicant = req.memberProfile?.user;
            const fullName = applicant
              ? `${applicant.firstName} ${applicant.middleName || ''} ${applicant.lastName}`.trim()
              : 'Unknown';
            const profile = req.memberProfile;
            const approvals = profile?.referralApprovals || [];
            const approvedCount = approvals.filter((a) => a.approved).length;

            return (
              <div key={req.id} className="card" style={{ padding: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 42, height: 42, borderRadius: '50%',
                      background: 'var(--primary)', color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 16
                    }}>
                      {applicant?.firstName?.[0]}{applicant?.lastName?.[0]}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--primary)' }}>{fullName}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{applicant?.email}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span className="badge badge-pending" style={{ fontSize: 12 }}>
                      <Clock size={11} style={{ marginRight: 4 }} />
                      Referral {req.referralNumber} — Awaiting Your Approval
                    </span>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                      {approvedCount} of 2 referrals approved
                    </div>
                  </div>
                </div>

                <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    {[
                      { label: 'Member ID', value: profile?.memberId },
                      { label: 'City', value: profile?.city },
                      { label: 'State', value: profile?.state },
                      { label: 'Degree', value: profile?.degree },
                      { label: 'University', value: profile?.university },
                      { label: 'Membership Type', value: profile?.membershipType },
                    ].map((item) => item.value && (
                      <div key={item.label}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{item.label}</div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label className="form-label">
                    Remarks <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                      (required for rejection, optional for approval)
                    </span>
                  </label>
                  <textarea className="form-input"
                    value={remarks[req.id] || ''}
                    onChange={(e) => setRemarks((prev) => ({ ...prev, [req.id]: e.target.value }))}
                    placeholder="Add your remarks here..." rows={2} />
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-success" onClick={() => handleApprove(req.id)}
                    disabled={!!actionLoading[req.id]} style={{ padding: '10px 24px' }}>
                    <UserCheck size={15} />
                    {actionLoading[req.id] === 'approve' ? 'Approving...' : 'Approve Request'}
                  </button>
                  <button className="btn btn-danger" onClick={() => handleReject(req.id)}
                    disabled={!!actionLoading[req.id]} style={{ padding: '10px 24px' }}>
                    <UserX size={15} />
                    {actionLoading[req.id] === 'reject' ? 'Rejecting...' : 'Reject Request'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
